<script lang="ts" setup>
import { ref, computed } from 'vue'
import {
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuPortal,
  DropdownMenuRoot,
  DropdownMenuTrigger,
} from 'radix-vue'
import Icon from '@/components/shared/Icon.vue'
import Button from '@/components/shared/Button.vue'
import SaveSearchModal from '@/components/shared/SaveSearchModal.vue'
import FieldText from '@/components/shared/Fields/FieldText.vue'
import { formatDate as formatDisplayDate } from '@/lib'
import { useSavedSearches } from '@/composables/useSavedSearches'
import type { SavedSearch } from '@/types/shared.types'
import { useSearch } from '@/composables/useSearch'
import { useForm } from 'vee-validate'

const props = defineProps<{
  currentSearchTerm: string
  currentSilos?: string[]
}>()

const emit = defineEmits<{
  selectSearch: [search: SavedSearch]
  saveCurrentSearch: []
}>()

const {
  savedSearches,
  saveSearch,
  deleteSearchByItem,
  updateSearchUsedTimeByItem,
  createFilteredSavedSearches,
  MAX_SAVED_SEARCHES,
} = useSavedSearches()
// Local filter driven by vee-validate form
const { values } = useForm({
  initialValues: {
    filter: '',
  },
})
const filteredSavedSearches = createFilteredSavedSearches(
  computed(() => (values.filter as string) || ''),
)

const { availableSilos } = useSearch()

// Helper to get silo names from IDs
function getSiloNames(siloIds: string[] | null): string {
  if (!siloIds || siloIds.length === 0) return ''

  const names = siloIds
    .map((id) => availableSilos.value.find((silo) => silo.value === id)?.label || id)
    .slice(0, 3) // Show max 3 silo names

  if (siloIds.length > 3) {
    return `${names.join(', ')} +${siloIds.length - 3} more`
  }

  return names.join(', ')
}

const showSaveDialog = ref(false)
const isSaving = ref(false)
const dropdownOpen = ref(false)

function handleSelectSearch(search: SavedSearch) {
  updateSearchUsedTimeByItem(search)
  emit('selectSearch', search)
}

async function handleDeleteSearch(index: number, event: Event) {
  event.stopPropagation()
  const search = filteredSavedSearches.value[index]
  if (search) await deleteSearchByItem(search)
}

function handleSaveClick() {
  if (!props.currentSearchTerm) return
  dropdownOpen.value = false
  // Add a small delay to allow dropdown animation to complete
  setTimeout(() => {
    showSaveDialog.value = true
  }, 100)
}

async function handleConfirmSave(searchName: string) {
  if (!searchName.trim()) return

  // Optimistically close the modal before awaiting network request
  isSaving.value = true
  showSaveDialog.value = false
  try {
    await saveSearch(searchName, props.currentSearchTerm, props.currentSilos || [])
  } catch (err) {
    // Error handled in composable
  } finally {
    isSaving.value = false
  }
}

function formatDate(dateString: string) {
  return formatDisplayDate(dateString, 'MMM d, yyyy')
}

const contentClass =
  'min-w-[18rem] max-w-[22rem] z-40 bg-white rounded-lg border border-gray-300 shadow-[0_10px_40px_10px_rgba(0,0,0,0.08)] will-change-[transform,opacity] data-[side=bottom]:animate-slideDownAndFade data-[side=left]:animate-slideLeftAndFade data-[side=right]:animate-slideRightAndFade data-[side=top]:animate-slideUpAndFade data-[state=closed]:animate-hide dark:bg-gray-900 dark:border-gray-700 dark:shadow-[0_10px_40px_10px_rgba(0,0,0,0.2)]'

const itemClass =
  'group cursor-pointer flex gap-x-2.5 py-1.5 px-2 text-[13px] text-gray-800 transition-colors hover:bg-gray-100 data-[highlighted]:bg-gray-100 disabled:opacity-50 disabled:pointer-events-none dark:text-gray-300 dark:hover:bg-gray-800 dark:data-[highlighted]:bg-gray-800 select-none focus:outline-none rounded-md'
</script>

<template>
  <div class="relative">
    <DropdownMenuRoot v-model:open="dropdownOpen">
      <DropdownMenuTrigger as-child>
        <Button type="primary" size="square" class="size-[32px]">
          <Icon variant="bookmark" class="size-[18px]" />
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuPortal to="#app-root">
        <DropdownMenuContent
          :class="contentClass"
          align="end"
          side="bottom"
          :side-offset="8"
          :collision-padding="10"
        >
          <div class="flex flex-col max-h-[28rem]">
            <!-- Sticky Header -->
            <div
              class="sticky top-0 px-3 pt-2.5 pb-2 space-y-2 bg-white rounded-t-lg border-b border-gray-200 dark:bg-gray-900 dark:border-gray-700"
            >
              <h3
                class="flex gap-x-2 items-center text-sm font-semibold text-gray-900 dark:text-gray-100"
              >
                <Icon variant="bookmark" class="size-4" />
                Saved Searches
              </h3>

              <div>
                <FieldText
                  variant="gray"
                  type="text"
                  :show-clear="true"
                  name="filter"
                  :show-label="false"
                  :validate="false"
                  prefix-icon="search"
                  color="default"
                  placeholder="Search saved searches..."
                />
              </div>
            </div>

            <!-- Scrollable List Container -->
            <div class="overflow-y-auto flex-1 p-1">
              <!-- Empty State -->
              <div
                v-if="filteredSavedSearches.length === 0 && savedSearches.length === 0"
                class="flex flex-col justify-center items-center px-4 py-6 text-center"
              >
                <Icon variant="bookmark" class="mb-3 text-gray-300 size-8 dark:text-gray-600" />
                <div class="mb-1 text-sm font-medium text-gray-900 dark:text-gray-100">
                  No saved searches yet
                </div>
                <div class="text-xs text-gray-500 dark:text-gray-400 max-w-48">
                  Save your search terms to quickly access them later
                </div>
              </div>
              <div
                v-else-if="filteredSavedSearches.length === 0"
                class="px-3 py-3 text-xs text-gray-500 dark:text-gray-400"
              >
                No matches found
              </div>

              <!-- Saved Searches List -->
              <template v-else>
                <DropdownMenuItem
                  v-for="(search, index) in filteredSavedSearches"
                  :key="`${search.Name}-${index}`"
                  @select="handleSelectSearch(search)"
                  :class="[itemClass, 'w-full items-start']"
                >
                  <!-- Left side: Name, Search term, Last used -->
                  <div class="flex-1 min-w-0">
                    <div class="mb-0.5 font-medium leading-tight text-gray-900 truncate dark:text-gray-100">
                      {{ search.Name }}
                    </div>
                    <div class="mb-0.5 font-mono text-xs text-gray-500 truncate dark:text-gray-400">
                      {{ search.Term }}
                    </div>
                    <div class="text-xs text-gray-500 dark:text-gray-400">
                      Last used: {{ formatDate(search.Used) }}
                    </div>
                  </div>

                  <!-- Right side: Silos and Trash -->
                  <div class="flex flex-col justify-between items-end self-stretch shrink-0">
                    <div class="flex gap-x-1 items-center text-xs text-gray-500 dark:text-gray-400">
                      <Icon variant="layers" class="size-3" />
                      <span class="max-w-[112px] truncate">{{ getSiloNames(search.Silos) }}</span>
                    </div>
                    <button
                      type="button"
                      class="self-end p-1 rounded border border-transparent opacity-0 transition-all group-hover:opacity-100 group-focus-within:opacity-100 hover:bg-error-50 hover:border-error-200 dark:hover:bg-error-900/20 dark:hover:border-error-800 default-focus"
                      @click.stop="handleDeleteSearch(index, $event)"
                    >
                      <Icon variant="trash" class="text-error-600 size-3.5 dark:text-error-400" />
                    </button>
                  </div>
                </DropdownMenuItem>
              </template>
            </div>

            <!-- Save Current Search Button at Bottom -->
            <div class="px-2 py-1.5 border-t border-gray-200 dark:border-neutral-700">
              <Button
                type="primary"
                size="sm"
                class="w-full"
                :disabled="!currentSearchTerm || savedSearches.length >= MAX_SAVED_SEARCHES"
                @click="handleSaveClick"
              >
                <Icon variant="plus" class="mr-2 shrink-0 size-3.5" />
                Save Current Search
                <span v-if="savedSearches.length >= MAX_SAVED_SEARCHES" class="ml-auto text-xs">
                  (Limit reached)
                </span>
              </Button>
            </div>
          </div>
        </DropdownMenuContent>
      </DropdownMenuPortal>
    </DropdownMenuRoot>

    <!-- Save Dialog -->
    <SaveSearchModal
      :open="showSaveDialog"
      :loading="isSaving"
      @close="showSaveDialog = false"
      @confirm="handleConfirmSave"
    />
  </div>
</template>
